/**
 * PTP (Promise to Pay) Form Component
 *
 * Conditional sub-form shown when outcome === 'ptp' in Phone Call Summary.
 * Uses React Hook Form (FormProvider) and allows adding multiple PTP entries.
 */

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useRef } from 'react';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useFieldArray, useFormContext } from 'react-hook-form';

import type { FieldErrors } from 'react-hook-form';
import { sanitizeDateValue, getDateInputError, DATE_INPUT_MAX } from '@/shared/utils/dateInputHelpers';

// ============================================================================
// Spacing and styling
// ============================================================================

const FIELD_LABEL_SPACING = 0.75;
const FIELD_ERROR_SPACING = 0.5;
const FORM_GROUP_GAP = 2;
const FORM_SECTION_PADDING = 2;

const labelSx = {
  mb: FIELD_LABEL_SPACING,
  display: 'block',
  fontSize: '0.75rem',
  color: 'text.secondary',
  fontWeight: 500,
};

const errorTextSx = {
  mt: FIELD_ERROR_SPACING,
  ml: 1,
  display: 'block',
  fontSize: '0.75rem',
  color: 'error.main',
};

// ============================================================================
// Field mapping (API field names -> form field names for server error mapping)
// ============================================================================

export const PTP_API_TO_FORM_FIELD_MAP: Record<string, string> = {
  'ptps.0.promised_date': 'ptps.0.promised_date',
  'ptps.0.promised_amount': 'ptps.0.promised_amount',
  'ptps.0.currency': 'ptps.0.currency',
  promised_date: 'ptps.0.promised_date',
  promised_amount: 'ptps.0.promised_amount',
  currency: 'ptps.0.currency',
};

// ============================================================================
// Types
// ============================================================================

export interface PtpFormEntry {
  promised_date: string;
  promised_amount: string;
  currency: string;
}

interface PtpFormProps {
  disabled?: boolean;
}

function getPtpFieldError(
  errors: FieldErrors,
  index: number,
  field: 'promised_date' | 'promised_amount' | 'currency'
): string | undefined {
  const ptpsErrors = errors['ptps'];
  if (!ptpsErrors || !Array.isArray(ptpsErrors)) return undefined;
  const entryErrors = ptpsErrors[index];
  if (!entryErrors || typeof entryErrors !== 'object') return undefined;
  const fieldError = (entryErrors as Record<string, { message?: string }>)[field];
  return fieldError?.message;
}

// ============================================================================
// Component
// ============================================================================

export function PtpForm({ disabled = false }: PtpFormProps) {
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ptps',
  });

  const handleAddPtp = () => {
    append({
      promised_date: '',
      promised_amount: '',
      currency: '',
    });
  };

  const hasInitialized = useRef(false);
  useEffect(() => {
    if (fields.length === 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      append({ promised_date: '', promised_amount: '', currency: '' });
    }
    if (fields.length > 0) {
      hasInitialized.current = false;
    }
  }, [fields.length, append]);

  return (
    <Box sx={{ mt: 2, mb: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: FORM_GROUP_GAP,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EventAvailableIcon sx={{ fontSize: 20 }} />
          <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
            Promise to Pay Details
          </Typography>
        </Box>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAddPtp}
          disabled={disabled}
        >
          Add PTP
        </Button>
      </Box>

      {fields.map((field, index) => {
        const dateError = getPtpFieldError(errors, index, 'promised_date');
        const amountError = getPtpFieldError(errors, index, 'promised_amount');
        const currencyError = getPtpFieldError(errors, index, 'currency');
        const dateReg = register(`ptps.${index}.promised_date`);
        const dateValue = watch(`ptps.${index}.promised_date`) as string | undefined;
        const dateValidationError = dateValue ? getDateInputError(dateValue) : null;
        const displayDateError = dateError || dateValidationError;

        return (
          <Box
            key={field.id}
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: FORM_GROUP_GAP,
              mb: FORM_GROUP_GAP,
              p: FORM_SECTION_PADDING,
              bgcolor: 'action.hover',
              borderRadius: 1.5,
              alignItems: 'flex-start',
            }}
          >
            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
              <Typography
                component="label"
                htmlFor={`ptp-${index}-date`}
                sx={labelSx}
              >
                Promised Date
              </Typography>
              <TextField
                name={dateReg.name}
                ref={dateReg.ref}
                onBlur={dateReg.onBlur}
                onChange={(e) => {
                  e.target.value = sanitizeDateValue(e.target.value);
                  dateReg.onChange(e);
                }}
                id={`ptp-${index}-date`}
                type="date"
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
                error={!!displayDateError}
                disabled={disabled}
                inputProps={{ 'aria-label': 'Promised Date', max: DATE_INPUT_MAX }}
              />
              {displayDateError && (
                <Typography variant="caption" sx={errorTextSx}>
                  {displayDateError}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
              <Typography
                component="label"
                htmlFor={`ptp-${index}-amount`}
                sx={labelSx}
              >
                Amount
              </Typography>
              <TextField
                {...register(`ptps.${index}.promised_amount`)}
                id={`ptp-${index}-amount`}
                type="number"
                fullWidth
                size="small"
                placeholder="Enter amount..."
                error={!!amountError}
                disabled={disabled}
                inputProps={{ min: 0, step: '0.01', 'aria-label': 'Promised Amount' }}
                onWheel={(e) => (e.target as HTMLElement).blur()}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                    e.preventDefault();
                  }
                }}
                sx={{
                  '& input[type=number]': { MozAppearance: 'textfield' },
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 },
                }}
              />
              {amountError && (
                <Typography variant="caption" sx={errorTextSx}>
                  {amountError}
                </Typography>
              )}
            </Box>

            <Box sx={{ flex: '0 1 100px', minWidth: 80 }}>
              <Typography
                component="label"
                htmlFor={`ptp-${index}-currency`}
                sx={labelSx}
              >
                Currency
              </Typography>
              <TextField
                {...register(`ptps.${index}.currency`)}
                id={`ptp-${index}-currency`}
                fullWidth
                size="small"
                placeholder="SGD"
                error={!!currencyError}
                disabled={disabled}
                inputProps={{ maxLength: 3, 'aria-label': 'Currency' }}
              />
              {currencyError && (
                <Typography variant="caption" sx={errorTextSx}>
                  {currencyError}
                </Typography>
              )}
            </Box>

            {fields.length > 1 && (
              <IconButton
                onClick={() => remove(index)}
                disabled={disabled}
                color="error"
                sx={{ mt: 1 }}
                aria-label="Remove PTP entry"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
